import { PropsWithChildren, useCallback, useContext, useLayoutEffect, useRef, forwardRef, ForwardedRef } from 'react';
import classnames from 'classnames';

import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useLayoutContext } from '@hh.ru/magritte-ui-grid/layoutContext';
import { TableContext } from '@hh.ru/magritte-ui-table/context';
import { TableCellProps } from '@hh.ru/magritte-ui-table/types';

import styles from './cell.less';
import tableStyles from './table.less';

const TableCellComponent = (
    {
        Element = 'td',
        m,
        l,
        xl,
        xxl,
        width,
        children,
        stickyLeft,
        stickyRight,
        align,
        'data-qa': dataQa = 'table-cell',
        ...rest
    }: PropsWithChildren<TableCellProps>,
    ref: ForwardedRef<HTMLTableCellElement>
) => {
    const cellRef = useRef<HTMLTableCellElement>(null);
    const multiRef = useMultipleRefs(ref, cellRef);
    const extendsL = l ?? m;
    const extendsXL = xl ?? extendsL;
    const extendsXXL = xxl ?? extendsXL;
    const { paddingRight, fontLoaded, subscribeResizeHandler, unsubscribeResizeHandler } = useContext(TableContext);
    const { leftSidebarEnabled, rightSidebarEnabled } = useLayoutContext();

    const calcCoordinates = useCallback(() => {
        if (cellRef.current && cellRef.current.parentElement) {
            cellRef.current.style.removeProperty('left');
            cellRef.current.style.removeProperty('right');
            const { left: parentLeft, right: parentRight } = cellRef.current.parentElement.getBoundingClientRect();
            const { right, left } = cellRef.current.getBoundingClientRect();
            if (stickyLeft) {
                cellRef.current.style.setProperty('left', `${left - parentLeft}px`);
            }

            if (stickyRight && cellRef.current.parentElement) {
                cellRef.current.style.setProperty('right', `${parentRight - right - (paddingRight ?? 0)}px`);
            }
        }
    }, [paddingRight, stickyRight, stickyLeft]);

    useLayoutEffect(() => {
        if (fontLoaded && (stickyLeft || stickyRight)) {
            calcCoordinates();
            subscribeResizeHandler?.(calcCoordinates);

            return () => unsubscribeResizeHandler?.(calcCoordinates);
        }

        return void 0;
    }, [
        stickyRight,
        stickyLeft,
        fontLoaded,
        paddingRight,
        calcCoordinates,
        subscribeResizeHandler,
        unsubscribeResizeHandler,
    ]);

    return (
        <Element
            {...rest}
            data-qa={dataQa}
            data-magritte-table-cell
            className={classnames(styles.cell, tableStyles.td, {
                [styles[`cell_m-${m}`]]: m,
                [styles[`cell_l-${extendsL}`]]: extendsL,
                [styles[`cell_xl-${extendsXL}`]]: extendsXL,
                [styles[`cell_xxl-${extendsXXL}`]]: extendsXXL,
                [styles.withLeftSidebar]: leftSidebarEnabled,
                [styles.withRightSidebar]: rightSidebarEnabled,
                [styles.sticky]: stickyLeft || stickyRight,
                [styles.stickyLeft]: stickyLeft,
                [styles.stickyRight]: stickyRight,
            })}
            ref={multiRef}
            style={{
                textAlign: align,
                width,
            }}
        >
            {children}
        </Element>
    );
};
export const TableCell = forwardRef(TableCellComponent);
